import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Skeleton,
  Link,
  Paper,
  alpha,
  Button,
  Collapse,
} from "@mui/material";
import WorkOffRounded from "@mui/icons-material/WorkOffRounded";
import TimelineRounded from "@mui/icons-material/TimelineRounded";
import AssignmentLateRounded from "@mui/icons-material/AssignmentLateRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRounded from "@mui/icons-material/TrendingDownRounded";
import { useQuery } from "@tanstack/react-query";
import { dashboardApi } from "../../services/api";
import type { FilterRequest, UtilizationInsightCard } from "../../types/api";
import { SEARCE_COLORS } from "../../theme";
import { CACHE_TIME } from "../../constants/QueryConstants";

export const UTILIZATION_SOLVER_INSIGHTS_SCROLL_ID = "utilization-solver-insights-section";

export type UtilizationInsightCardId =
  | "no-client-alloc-1yr"
  | "no-allocation-90d"
  | "no-timesheet-30d"
  | "allocated-over-100"
  | "allocated-under-20-1yr";

export function getUtilizationInsightTableFilterPatch(
  _cardId: UtilizationInsightCardId,
  matchingEmails: string[]
): { emailAllowList: string[] } {
  return { emailAllowList: matchingEmails.map((e) => e.trim().toLowerCase()).filter(Boolean) };
}

type InsightTone = "idle" | "pipeline" | "compliance" | "over" | "under";

interface InsightCardDef {
  id: UtilizationInsightCardId;
  badge: string;
  tone: InsightTone;
  title: string;
  subtext: string;
  actionLabel: string;
  icon: React.ReactNode;
}

const CARD_DEFS: InsightCardDef[] = [
  {
    id: "no-client-alloc-1yr",
    badge: "LONG-TERM IDLE",
    tone: "idle",
    title: "No client allocation · 1 yr",
    subtext:
      "Active solvers with zero client allocated project hours in the past 12 months.",
    actionLabel: "View solvers →",
    icon: <WorkOffRounded />,
  },
  {
    id: "no-allocation-90d",
    badge: "PIPELINE GAP",
    tone: "pipeline",
    title: "No allocation · 3+ months",
    subtext: "Solvers with no project allocation in the next 90+ days — forward bench risk.",
    actionLabel: "View solvers →",
    icon: <TimelineRounded />,
  },
  {
    id: "no-timesheet-30d",
    badge: "COMPLIANCE",
    tone: "compliance",
    title: "No timesheet · 30 days",
    subtext: "Active solvers with zero Zoho timesheet entries in the last 30 days.",
    actionLabel: "View solvers →",
    icon: <AssignmentLateRounded />,
  },
  {
    id: "allocated-over-100",
    badge: "OVER-ALLOCATED",
    tone: "over",
    title: "Allocated >100%",
    subtext: "Active solvers with allocation greater than 100%.",
    actionLabel: "View solvers →",
    icon: <TrendingUpRounded />,
  },
  {
    id: "allocated-under-20-1yr",
    badge: "UNDER-UTILIZED",
    tone: "under",
    title: "Allocated <20% · 1 yr",
    subtext: "Active solvers with allocation less than 20% in the last 1 year.",
    actionLabel: "View solvers →",
    icon: <TrendingDownRounded />,
  },
];

const toneColors: Record<
  InsightTone,
  { border: string; iconBg: string; iconFg: string; badgeBg: string }
> = {
  idle: {
    border: SEARCE_COLORS.error,
    iconBg: alpha(SEARCE_COLORS.error, 0.12),
    iconFg: SEARCE_COLORS.error,
    badgeBg: alpha(SEARCE_COLORS.error, 0.12),
  },
  pipeline: {
    border: SEARCE_COLORS.warning,
    iconBg: alpha(SEARCE_COLORS.warning, 0.18),
    iconFg: SEARCE_COLORS.warningDark,
    badgeBg: alpha(SEARCE_COLORS.warning, 0.2),
  },
  compliance: {
    border: SEARCE_COLORS.info,
    iconBg: alpha(SEARCE_COLORS.info, 0.12),
    iconFg: SEARCE_COLORS.info_700,
    badgeBg: alpha(SEARCE_COLORS.info, 0.14),
  },
  over: {
    border: SEARCE_COLORS.secondary,
    iconBg: alpha(SEARCE_COLORS.secondary, 0.12),
    iconFg: SEARCE_COLORS.secondary_600,
    badgeBg: alpha(SEARCE_COLORS.secondary, 0.12),
  },
  under: {
    border: "#C9A227",
    iconBg: alpha("#C9A227", 0.18),
    iconFg: "#8A6B00",
    badgeBg: alpha("#C9A227", 0.2),
  },
};

const WATCH_TABS: { id: UtilizationInsightCardId; label: string }[] = [
  { id: "no-client-alloc-1yr", label: "No client alloc · 1 yr" },
  { id: "no-allocation-90d", label: "No allocation · 90 days" },
  { id: "no-timesheet-30d", label: "No timesheet · 30 days" },
  { id: "allocated-over-100", label: "Allocated >100%" },
  { id: "allocated-under-20-1yr", label: "Allocated <20% · 1 yr" },
];

const WATCH_TAB_FOOTER: Record<UtilizationInsightCardId, string> = {
  "no-client-alloc-1yr":
    "Sorted by longest duration with zero client hours · source: SFDC + utilization warehouse.",
  "no-allocation-90d":
    "No In Progress SFDC allocation in the next ~90 days · source: mv_employee_allocation_monthly.",
  "no-timesheet-30d":
    "Sorted by days since last approved Zoho log · source: timelogs.",
  "allocated-over-100":
    "Current-month allocation % above 100 · source: utilization warehouse.",
  "allocated-under-20-1yr":
    "Trailing 12-month allocation % below 20 · source: utilization warehouse.",
};

function WatchListRows({
  rows,
  accentColor,
  emptyHint,
}: {
  rows: UtilizationInsightCard["watchlist"];
  accentColor: string;
  emptyHint: string;
}) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
        {emptyHint}
      </Typography>
    );
  }
  return (
    <>
      {rows.map((row, idx) => (
        <Box
          key={row.email}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderTop: idx === 0 ? "none" : "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 4,
              alignSelf: "stretch",
              borderRadius: 1,
              bgcolor: accentColor,
              flexShrink: 0,
            }}
          />
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(accentColor, 0.12),
              color: accentColor,
              fontWeight: 800,
              fontSize: 14,
            }}
          >
            {idx + 1}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap title={row.name}>
              {row.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
              title={row.subtitle}
            >
              {row.subtitle}
            </Typography>
          </Box>
          <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, textAlign: "right" }}>
            {row.metric_label}
          </Typography>
        </Box>
      ))}
    </>
  );
}

export interface UtilizationActionableInsightsProps {
  filters: FilterRequest;
  queriesEnabled: boolean;
  onNavigateWithInsight?: (cardId: UtilizationInsightCardId, matchingEmails: string[]) => void;
}

const UtilizationActionableInsights: React.FC<UtilizationActionableInsightsProps> = ({
  filters,
  queriesEnabled,
  onNavigateWithInsight,
}) => {
  const [watchTab, setWatchTab] = useState<UtilizationInsightCardId>("no-client-alloc-1yr");
  const [calcOpen, setCalcOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["utilization-actionable-insights", filters],
    queryFn: () => dashboardApi.getUtilizationActionableInsights(filters),
    staleTime: CACHE_TIME,
    enabled: queriesEnabled,
  });

  const insightById = useMemo(() => {
    const map = new Map<string, UtilizationInsightCard>();
    for (const ins of data?.insights ?? []) {
      map.set(ins.id, ins);
    }
    return map;
  }, [data?.insights]);

  const handleInsightLinkClick = (
    e: React.MouseEvent,
    cardId: UtilizationInsightCardId
  ) => {
    e.preventDefault();
    const emails = insightById.get(cardId)?.matching_emails ?? [];
    onNavigateWithInsight?.(cardId, emails);
  };

  const watchTabDef = CARD_DEFS.find((c) => c.id === watchTab) ?? CARD_DEFS[0];
  const watchAccent = toneColors[watchTabDef.tone].border;
  const watchRows = insightById.get(watchTab)?.watchlist ?? [];

  if (!queriesEnabled) {
    return null;
  }

  return (
    <Box sx={{ mb: 0 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Actionable insights
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 960 }}>
        Solver-level signals not visible elsewhere on this page — proactive risks around chronic
        bench, over-allocation, compliance, and low utilization. Scoped to active filters.
      </Typography>

      <Typography variant="caption" fontWeight={700} letterSpacing={0.08} color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
        INSIGHT CATALOG
      </Typography>

      {isLoading ? (
        <Box sx={{ display: "flex", gap: 2, overflow: "hidden" }}>
          {[1, 2, 3, 4, 5].map((k) => (
            <Skeleton
              key={k}
              variant="rounded"
              sx={{ minWidth: 220, height: 168, flexShrink: 0, borderRadius: 2 }}
              animation="wave"
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            pb: 1,
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {CARD_DEFS.map((card) => {
            const c = toneColors[card.tone];
            const count = insightById.get(card.id)?.count ?? 0;
            return (
              <Paper
                key={card.id}
                elevation={0}
                sx={{
                  minWidth: { xs: 240, sm: 220 },
                  maxWidth: 280,
                  flex: "0 0 auto",
                  scrollSnapAlign: "start",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderTop: `4px solid ${c.border}`,
                  bgcolor: "background.paper",
                  boxShadow: (theme) => theme.shadows[1],
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: c.iconBg,
                        color: c.iconFg,
                        "& .MuiSvgIcon-root": { fontSize: 17 },
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.05, letterSpacing: -0.02 }}>
                      {count}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: c.badgeBg,
                      color: c.iconFg,
                      flexShrink: 0,
                      fontSize: 10,
                    }}
                  >
                    {card.badge}
                  </Typography>
                </Box>
                <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, fontSize: 13 }}>
                  {card.subtext}
                </Typography>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  disabled={!onNavigateWithInsight || count === 0}
                  onClick={(e) => handleInsightLinkClick(e, card.id)}
                  sx={{ alignSelf: "flex-start", fontWeight: 600, cursor: "pointer" }}
                >
                  {card.actionLabel}
                </Link>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* {isLoading ? (
        <Skeleton variant="rounded" animation="wave" sx={{ mt: 3, height: 280, borderRadius: 2 }} />
      ) : (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              mb: 1.5,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Solvers to watch
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Top 5 per signal · tabs switch the lens
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              borderLeft: `4px solid ${watchAccent}`,
              bgcolor: "background.paper",
              boxShadow: (theme) => theme.shadows[1],
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                px: 2,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
              }}
            >
              {WATCH_TABS.map((t) => {
                const selected = watchTab === t.id;
                return (
                  <Button
                    key={t.id}
                    type="button"
                    variant="text"
                    disableElevation
                    onClick={() => setWatchTab(t.id)}
                    sx={{
                      borderRadius: 999,
                      px: 2,
                      py: 0.65,
                      minWidth: 0,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13,
                      lineHeight: 1.25,
                      border: "1px solid",
                      borderColor: selected ? "primary.main" : "divider",
                      bgcolor: selected ? "primary.main" : "background.paper",
                      color: selected ? "primary.contrastText" : "text.primary",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: selected ? "primary.dark" : (theme) => alpha(theme.palette.primary.main, 0.06),
                        borderColor: selected ? "primary.dark" : "text.disabled",
                      },
                    }}
                  >
                    {t.label}
                  </Button>
                );
              })}
            </Box>

            <Box sx={{ pt: 1 }}>
              <WatchListRows
                rows={watchRows}
                accentColor={watchAccent}
                emptyHint="No solvers match this signal for the current filters."
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 560 }}>
                {WATCH_TAB_FOOTER[watchTab]}
              </Typography>
              <Link
                component="button"
                type="button"
                variant="caption"
                disabled={!onNavigateWithInsight || (insightById.get(watchTab)?.count ?? 0) === 0}
                onClick={(e) => handleInsightLinkClick(e, watchTab)}
                sx={{ fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                View in Solver Insights
              </Link>
            </Box>
          </Paper>
        </Box>
      )} */}

      <Link
        component="button"
        type="button"
        variant="body2"
        onClick={() => setCalcOpen((o) => !o)}
        sx={{ mt: 2, fontWeight: 600, cursor: "pointer" }}
      >
        {calcOpen ? "− Hide" : "+ How these metrics are calculated"}
      </Link>
      <Collapse in={calcOpen}>
        <Typography component="ul" variant="body2" color="text.secondary" sx={{ mt: 1, pl: 2.5, mb: 0 }}>
          <li>
            <strong>No client allocation · 1 yr:</strong> zero client billable allocated hours across the
            trailing 12 calendar months.
          </li>
          <li>
            <strong>No allocation · 90 days:</strong> no In Progress SFDC allocation with hours in the current
            month plus the next three months (~90 days forward).
          </li>
          <li>
            <strong>No timesheet · 30 days:</strong> zero approved Zoho hours in the last 30 days.
          </li>
          <li>
            <strong>Allocated &gt;100%:</strong> current-month total allocated hours exceed potential hours.
          </li>
          <li>
            <strong>Allocated &lt;20% · 1 yr:</strong> trailing 12-month allocated % of potential below 20%.
          </li>
        </Typography>
      </Collapse>
    </Box>
  );
};

export default UtilizationActionableInsights;
